import { create } from "zustand";
import { toast } from "react-toastify";
import {
  COMPLAINT_ENDPOINTS,
  getData,
  postData,
  putData,
} from "../../../../Apis/complaintApi";
import {
  COMPLAINT_MAX_FETCH_SIZE,
  sortActiveFirst,
} from "../../utils/complaintListUtils";

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data) return data;
  return data?.message || data?.Message || error?.message || fallback;
};

const toDisplayDateTime = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const milliseconds = String(date.getMilliseconds()).padStart(3, "0");

  return `${day}-${month}-${year}T${hours}:${minutes}:${seconds}.${milliseconds}`;
};

export const formatDisplayDate = (dateValue) => {
  if (!dateValue) return toDisplayDateTime(new Date());

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;

  return toDisplayDateTime(date);
};

const applyIeKpiTypeSearch = (data, searchTerm) => {
  if (!searchTerm?.trim()) return data;

  const term = searchTerm.trim().toLowerCase();

  return data.filter(
    (item) =>
      item?.kpiType?.toLowerCase()?.includes(term) ||
      item?.kpiCode?.toLowerCase()?.includes(term)
  );
};

const mapIeKpiTypeList = (response) => {
  if (!response) return [];

  const list =
    response?.data ||
    response?.result ||
    response?.items ||
    response;

  return Array.isArray(list) ? list : [];
};

export const useIEKpiTypeStore = create((set, get) => ({
  ieKpiTypes: [],
  filterOriginalIeKpiTypes: [],
  filteredIeKpiTypes: [],
  isLoading: false,

  filters: {
    searchTerm: "",
  },

  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
  },

  updateFilter: (name, value) =>
    set((state) => ({
      filters: {
        ...state.filters,
        [name]: value,
      },
    })),

  applyFilters: (data, currentFilters) => {
    const filters = currentFilters ?? get().filters;

    const sourceData =
      data ??
      get().filterOriginalIeKpiTypes ??
      get().ieKpiTypes;

    const result = applyIeKpiTypeSearch(
      sourceData,
      filters.searchTerm
    );

    set({
      filteredIeKpiTypes: result,
    });

    return result;
  },

  fetchIeKpiTypes: async () => {
    const { filters } = get();

    set({ isLoading: true });

    try {
      const response = await getData(
        COMPLAINT_ENDPOINTS.GET_IE_KPI_TYPE,
        {
          PageIndex: 1,
          PageSize: COMPLAINT_MAX_FETCH_SIZE,
        }
      );

      const sorted = sortActiveFirst(mapIeKpiTypeList(response));

      set({
        ieKpiTypes: sorted,
        filterOriginalIeKpiTypes: sorted,
        filteredIeKpiTypes: applyIeKpiTypeSearch(sorted, filters.searchTerm),
        pagination: {
          currentPage: 1,
          pageSize: sorted.length || 10,
          totalCount: sorted.length,
        },
        isLoading: false,
      });

      return {
        success: true,
        data: sorted,
      };
    } catch (error) {
      console.error("Error fetching IE KPI Types:", error);

      set({
        ieKpiTypes: [],
        filterOriginalIeKpiTypes: [],
        filteredIeKpiTypes: [],
        pagination: {
          currentPage: 1,
          pageSize: 10,
          totalCount: 0,
        },
        isLoading: false,
      });

      return {
        success: false,
        error,
      };
    }
  },
  addIeKpiType: async (values) => {
    try {
      set({ isLoading: true });

      const payload = {
        kpiTypeName: values.kpiType,
        kpiCode: values.kpiCode,
        isActive: values.isActive,
      };

      const response = await postData(
        COMPLAINT_ENDPOINTS.ADD_IE_KPI_TYPE,
        payload
      );

      toast.success(
        response?.message || "KPI Type added successfully."
      );

      await get().fetchIeKpiTypes();

      set({ isLoading: false });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      set({ isLoading: false });
      toast.error(
        getErrorMessage(error, "Unable to add KPI Type.")
      );

      return {
        success: false,
        error,
      };
    }
  },

  updateIeKpiType: async (values) => {
    try {
      set({ isLoading: true });

      const payload = {
        kpiTypeId: values.kpiTypeId,
        kpiTypeName: values.kpiType,
        kpiCode: values.kpiCode,
        isActive: values.isActive,
      };

      const response = await putData(
        COMPLAINT_ENDPOINTS.UPDATE_IE_KPI_TYPE,
        payload
      );

      toast.success(
        response?.message || "KPI Type updated successfully."
      );

      await get().fetchIeKpiTypes();

      set({ isLoading: false });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      set({ isLoading: false });
      toast.error(
        getErrorMessage(error, "Unable to update KPI Type.")
      );

      return {
        success: false,
        error,
      };
    }
  },
}));